import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, Bell, Lock, Palette, Shield, Briefcase, LogOut, BadgeCheck
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Card from '../components/common/Card';
import ProfileSettings from '../components/settings/ProfileSettings';
import NotificationSettings from '../components/settings/NotificationSettings';
import PrivacySettings from '../components/settings/PrivacySettings';
import AppearanceSettings from '../components/settings/AppearanceSettings';
import SecuritySettings from '../components/settings/SecuritySettings';
import SubscriptionSettings from '../components/settings/SubscriptionSettings';
import VerificationSettings from '../components/settings/VerificationSettings';

type SettingsTab = 'profile' | 'notifications' | 'privacy' | 'appearance' | 'security' | 'subscription' | 'verification';

export default function SettingsPage() {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <User className="h-5 w-5" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="h-5 w-5" /> },
    { id: 'privacy', label: 'Privacy', icon: <Lock className="h-5 w-5" /> },
    { id: 'appearance', label: 'Appearance', icon: <Palette className="h-5 w-5" /> },
    { id: 'security', label: 'Security', icon: <Shield className="h-5 w-5" /> },
    { id: 'subscription', label: 'Subscription', icon: <Briefcase className="h-5 w-5" /> },
    { id: 'verification', label: 'Verification', icon: <BadgeCheck className="h-5 w-5" /> }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings />;
      case 'notifications':
        return <NotificationSettings />;
      case 'privacy':
        return <PrivacySettings />;
      case 'appearance':
        return <AppearanceSettings />;
      case 'security':
        return <SecuritySettings />;
      case 'subscription':
        return <SubscriptionSettings />;
      case 'verification':
        return <VerificationSettings />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full md:w-64 space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as SettingsTab)}
              className={`
                w-full flex items-center space-x-3 px-4 py-3 rounded-lg
                transition-colors
                ${activeTab === tab.id
                  ? theme === 'dark'
                    ? 'bg-gray-800 text-white'
                    : 'bg-white text-gray-900 shadow-sm'
                  : theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-sm'
                }
              `}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}

          <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => console.log('Logout')}
              className={`
                w-full flex items-center space-x-3 px-4 py-3 rounded-lg
                transition-colors text-red-600 hover:text-red-700
                ${theme === 'dark'
                  ? 'hover:bg-gray-800'
                  : 'hover:bg-white hover:shadow-sm'
                }
              `}
            >
              <LogOut className="h-5 w-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <Card className="p-6">
            <h2 className={`text-xl font-semibold mb-6 ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {tabs.find(tab => tab.id === activeTab)?.label}
            </h2>
            {renderContent()}
          </Card>
        </div>
      </div>
    </div>
  );
}